import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { Dialog, DialogContent, DialogContentText, DialogActions, Button, Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import useConfig from './hooks/useConfig';
import apiRequest from './hooks/useApi';
import { getAuthPortalLoginUrl } from './utils/authPortal';
import { clearIdentityCookiesAllScopes } from './utils/clearIdentityCookieScopes';
import {
  applyAccountContext,
  buildContextFromRoleEntry,
  getDefaultContext,
  getActiveContext,
  getUserRoleEntryForContext,
  isSecondaryAdminSession,
  parseAccountContextsFromLoginResponse,
  persistAccountContexts,
  persistLoginSnapshot,
  persistPostLoginPending,
  shouldShowAccountPicker,
  resolveUserRoleKey,
  sanitizePersonName,
  supportAccessStForAccount
} from './utils/accountContext/accountContextUtils';
import { consumeRumLoginFlow, persistRumLoginFlow, recordRumUserLogin, setRumSessionIdentity } from './rum/rumAnalytics';
import questionMarkDarkTheme from './assets/images/questionMarkDarkTheme.svg';
import { applyNewUserOnboardingScope, getPostLoginLanding, persistPostLoginScope } from './utils/postLoginLanding';
import dialogEventEmitter from './utils/dialogEventEmitter';
import { useOrganization } from './contexts/OrganizationContext';
const cookies = new Cookies();

const isPayment = process.env.isPaymentneeded;
const supportAccessLevel = ['s3', 's2', 's1'];
const DEFAULT_SYNC_LEAD_SECONDS = 60;

function isSessionWarningActive(sessionInfo) {
  return sessionInfo?.sessionWarningActive === '1' || sessionInfo?.sessionWarningActive === 1;
}

function hasActiveSupportImpersonationSession(loginData = {}) {
  const sessionStatus = loginData.supportUserInfo?.sessionStatus ?? loginData.sessionStatus;
  return sessionStatus === '1' || sessionStatus === 1;
}

/**
 * Only start the inactivity timer when there is a real session TTL or an explicit warning.
 * TTL 0 with no active session means "no impersonation session" — not "expired".
 */
function shouldEmitSupportSessionTtl(sessionInfo, loginData = {}) {
  if (!sessionInfo || sessionInfo.sessionSecondsRemaining === undefined) {
    return false;
  }

  const ttl = Number(sessionInfo.sessionSecondsRemaining);
  if (Number.isNaN(ttl)) {
    return false;
  }

  if (isSessionWarningActive(sessionInfo)) {
    return true;
  }

  if (ttl <= 0) {
    return hasActiveSupportImpersonationSession(loginData);
  }

  return true;
}

/**
 * Emit support session TTL from postUserLogin data (own account or impersonation).
 */
function emitSupportSessionTtlFromPostUserLogin(loginData) {
  if (!loginData) return;

  const ttlSource =
    loginData.supportUserInfo?.sessionSecondsRemaining !== undefined
      ? loginData.supportUserInfo
      : loginData.sessionSecondsRemaining !== undefined
        ? loginData
        : null;

  if (!ttlSource) {
    return;
  }

  if (!shouldEmitSupportSessionTtl(ttlSource, loginData)) {
    return;
  }

  emitSupportSessionTtl(ttlSource, 'postUserLogin');
}

/**
 * Emit support session TTL info if present.
 * Starts the frontend inactivity timer for support users (own account or impersonating).
 */
function emitSupportSessionTtl(sessionInfo, _source = 'unknown') {
  if (!sessionInfo || sessionInfo.sessionSecondsRemaining === undefined) {
    return;
  }

  const ttl = Number(sessionInfo.sessionSecondsRemaining);
  const warningActive = isSessionWarningActive(sessionInfo);

  if (Number.isNaN(ttl) || (ttl <= 0 && !warningActive)) {
    return;
  }

  const ttlData = {
    sessionSecondsRemaining: ttl,
    sessionWarningActive: warningActive,
    sessionTtlSyncLeadSeconds: Number(sessionInfo.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
  };

  dialogEventEmitter.emit('supportSessionTtlUpdate', ttlData);
}

function bootstrapSupportSessionTimer() {
  const accessLevel = localStorage.getItem('accessLevel') || '';
  if (supportAccessLevel.includes(accessLevel) || cookies.get('userSubRole')) {
    dialogEventEmitter.emit('supportSessionBootstrap');
  }
}

const cookieSecure = () => Boolean(process.env.secureCoockie);

function resolveRumActorType(accessLevel, isSupportSession = false) {
  return isSupportSession || supportAccessLevel.indexOf(accessLevel) >= 0 ? 'support_agent' : 'customer';
}

function syncRumAuthenticatedSession({ accountId, email, insightUserId, userRole, actorType, loginFlow }) {
  setRumSessionIdentity({
    accountId,
    userEmail: email,
    insightUserId,
    userRole,
    actorType
  });

  recordRumUserLogin({
    userEmail: email,
    insightUserId,
    effectiveAccountId: accountId,
    loginFlow
  });
}

function CamSDK() {
  const navigate = useNavigate();
  const [emailID, setEmailID] = useState('');
  const { showLoader, hideLoader } = useConfig();
  const { updateOrganizationContext } = useOrganization();
  const intl = useIntl();
  // Informative active-session dialog: shown when the agent tries to reach their Insight
  // account while a support session is still active. Matches the imuserportal legacy UI —
  // it only informs the user and never ends the session.
  const [activeSessionDialog, setActiveSessionDialog] = useState({ open: false });
  // Consume the one-shot "Go to my account" intent on mount so a stale flag can't trigger
  // the informative active-session dialog on a later, unrelated auth pass.
  const goToMyAccIntentRef = useRef(false);
  useEffect(() => {
    goToMyAccIntentRef.current = localStorage.getItem('goToMyAcc') === '1';
    localStorage.removeItem('goToMyAcc');
    showLoader();
    checkAuthWithCookies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v3 flow: session is in HttpOnly cookies. Check auth/me; on success getApiKey(), else auth portal login.
  const checkAuthWithCookies = async () => {
    const authMeUrl = `https://${process.env.authUrl}/v1/auth/me`;
    try {
      const response = await axios.get(authMeUrl, { withCredentials: true, timeout: 10000 });
      if (response?.data && response.status === 200) {
        const d = response.data;
        const email = d.email;
        // OC may expose the One Cloud id under preferredUsername, username, or sub; if we only set email
        // and skip userId, stale localStorage/cookies can pair with new HttpOnly tokens and postUserLogin fails.
        const userId = d.preferredUsername || d.username || d.sub || d.oneCloudUserId;
        if (!email || !userId) {
          hideLoader();
          window.location.href = getAuthPortalLoginUrl();
          return;
        }
        localStorage.removeItem('postUserLoginFailMessage');
        // Drop host + .netgear.com duplicates so postUserLogin matches auth/me (not a stale prod cookie).
        clearIdentityCookiesAllScopes(cookies);
        cookies.set('user_email', email, { secure: cookieSecure(), path: '/' });
        localStorage.setItem('user_email', email);
        // Keep legacy `email` in sync — many flows read this key; mismatch with user_email breaks postUserLogin and UI.
        localStorage.setItem('email', email);
        cookies.set('userId', userId, { secure: cookieSecure(), path: '/' });
        localStorage.setItem('userId', userId);
        // UserId (PascalCase) is the Insight id from postUserLogin; until that runs again it may be another user's id.
        localStorage.removeItem('UserId');
        getApiKey();
        return;
      }
    } catch (_) {
      // auth/me failed (no cookie session)
    }
    hideLoader();
    window.location.href = getAuthPortalLoginUrl();
  };

  const checkOrganizationList = async (pendingFirstLoginTourGuide = false) => {
    try {
      // The total count distinguishes single-org and multi-org users; one row is
      // enough because the complete hierarchy is fetched only for a single org.
      const response = await apiRequest('GET_MSP_ORG_DETAILS', { page: 0, limit: 1, searchText: '' }, {}, []);

      // Check if organization list is empty
      const orgCount = Number(response?.details?.count) || 0;
      const startGuidedTour = pendingFirstLoginTourGuide;
      const secondaryAdminSession = isSecondaryAdminSession();

      if (orgCount === 0) {
        hideLoader();
        // Invited secondary admins share the primary account. An empty org API is a
        // scoping miss, not a first-time create-org flow. Support impersonation of a
        // manager/secondary must not start create-org either — attach to the managed
        // admin account instead of treating a personal stub as a new tenant.
        const supportImpersonation = !!cookies.get('userSubRole');
        if (secondaryAdminSession || supportImpersonation) {
          localStorage.setItem('userHadOrganizationsAtLogin', 'true');
          applyNewUserOnboardingScope(updateOrganizationContext);
          navigate('/mspHome/dashboard');
          bootstrapSupportSessionTimer();
          return;
        }
        localStorage.setItem('userHadOrganizationsAtLogin', 'false');
        // New user has no org — mark scope as resolved at MSP level so
        // CloudRestApiAccessProvider's supportedFeatures fetch guard releases.
        // (scopeResolved starts false when localStorage has no scopeId; without
        // this call it never flips true and supportedFeatures never fires.)
        applyNewUserOnboardingScope(updateOrganizationContext);
        navigate('/newLogin');
      } else {
        localStorage.setItem('userHadOrganizationsAtLogin', 'true');

        let organizations = [];
        if (orgCount === 1) {
          // The paginated org list does not contain the location records needed to
          // distinguish an organization landing from a location landing.
          try {
            const hierarchyResponse = await apiRequest('GET_ORG_CHILD_DETAILS', {}, {}, ['_Id', 'networkId']);
            if (hierarchyResponse?.response?.status && Array.isArray(hierarchyResponse.info)) {
              const onlyOrgId = response?.info?.[0]?.orgId;
              const matchingOrganization = hierarchyResponse.info.find((organization) => String(organization.orgId) === String(onlyOrgId));
              organizations = matchingOrganization
                ? [matchingOrganization]
                : hierarchyResponse.info.length === 1
                  ? hierarchyResponse.info
                  : [];
            }
          } catch (hierarchyError) {
            // Do not send an existing user back through onboarding because the
            // optional hierarchy lookup failed; retain the established MSP landing.
            console.error('Error checking organization locations:', hierarchyError);
          }
        }

        const landing = getPostLoginLanding(orgCount, organizations);
        const landingScope = persistPostLoginScope(landing);
        if (landingScope) {
          // OrganizationProvider is mounted before authentication completes. Update
          // its live state as manual hierarchy selection does; dashboard summary APIs
          // read this context and would otherwise issue their first request at MSP scope.
          updateOrganizationContext(landingScope.orgId, landingScope.scopeId, landingScope.currentNetwork, landingScope.selectedType);
        }
        hideLoader();

        // Existing users skip "You're All Set"; the dashboard shell consumes startTour.
        const destination = startGuidedTour ? `${landing.path}?startTour=true` : landing.path;
        navigate(destination);
        bootstrapSupportSessionTimer();
      }
    } catch (error) {
      console.error('Error checking organization list:', error);
      // On error, continue rendering (don't block the dashboard)
      hideLoader();
      if (pendingFirstLoginTourGuide) {
        navigate('/newLogin?firstLoginTour=1');
      } else {
        navigate('/mspHome/dashboard');
        bootstrapSupportSessionTimer();
      }
    }
  };

  const redirectProAdmin = (pendingFirstLoginTourGuide = false) => {
    if (cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices') {
      cookies.set('toggleSrc', 'insightApp', { secure: process.env.secureCoockie || true });
      // window.location.href = "/#/insightServices";
      cookies.remove('redirectPlatform');
      cookies.remove('redirectPage');
    } else if (cookies.get('existingProAcnt') == 'true') {
      // window.location.href = "/#/accountManagement/subScriptionsPro";
      cookies.remove('existingProAcnt');
    } else {
      // window.location.href = "/#/mspHome";
      checkOrganizationList(pendingFirstLoginTourGuide);
    }
  };
  // An admin whose account still has networks belonging to no organization gets responseCode
  // 11105 here. The classic UI answered that by navigating to /#/premiumToPro/addLocToOrg;
  // that screen has no MUI equivalent, and the branch was left empty — so login stopped dead
  // with the loader still mounted. Record the state in the cookie as before, then land the
  // admin on their dashboard either way. The lookup is advisory: it never decides whether the
  // user may log in, so no failure of it may block the redirect.
  const resolveLocationAssignmentThenRedirect = async (email, cookieOptions, pendingFirstLoginTourGuide = false) => {
    try {
      const Netresult = (
        await axios.get(`${process.env.apiHost}organization/v1/getnetworkwithoutorg/${cookies.get('_Id')}`, {
          headers: {
            accountId: cookies.get('userAccountId'),
            'Content-Type': 'application/json; charset=UTF-8',
            Accept: 'application/json'
          },
          withCredentials: true,
          timeout: 60000
        })
      ).data;

      if (Netresult?.response?.status == true) {
        // 11105 NETWORKS_PRESENT_WITH_NO_ORGANIZATION, 11106 ALL_NETWORKS_ASSIGNED
        const locationsNeedAssigning = Netresult.response.responseCode == 11105;
        cookies.set(
          'isLocNeedToAssg',
          CryptoJS.AES.encrypt(locationsNeedAssigning ? 'yes' : 'no', email.split('@')[0].toLowerCase()).toString(),
          cookieOptions
        );
      }
    } catch (err) {
      // Do not fall through to getApiKey's catch: it reads an errorless axios failure as an
      // expired session and bounces to the auth portal, which loops on a valid session.
      console.error('[login] getnetworkwithoutorg failed; continuing to dashboard', err);
    }
    redirectProAdmin(pendingFirstLoginTourGuide);
  };

  const getVoucherAdminLastVistedPage = async (_role) => {
    const result = (
      await axios.get(`${process.env.apiHost}user/v1/lastvisit/${cookies.get('_Id')}`, {
        headers: {
          accountId: cookies.get('accountId'),
          'Content-Type': 'application/json; charset=UTF-8',
          Accept: 'application/json'
        },
        withCredentials: true,
        timeout: 60000
      })
    ).data;
    if (result.response.status) {
      const data = JSON.parse(result.details.path);
      localStorage.setItem('currentOrg', data.selectedOrg);
      localStorage.setItem('currentNetwork', data.selectedLoc);
      cookies.set('organization_Id', data.selectedOrg, { secure: cookieSecure() });
      // window.location.href = "/"+data.path;
    }
    // else {
    //   if (role == "5")
    //     window.location.href = "/#/voucherManagement/organisationView"
    //   else
    //     window.location.href = "/#/voucherManagement/locationView/all"
    // }
  };
  const loginAsUser = async () => {
    const userDetails = JSON.parse(localStorage.getItem('userToLogin'));
    localStorage.removeItem('userToLogin');
    const loginFlow = consumeRumLoginFlow('support_login_as');
    // this.setState({ emailID: userDetails.email })
    setEmailID(userDetails.email);
    let userType = userDetails.userRole;
    if (userType == 1) {
      userType = 'admin';
    } else if (userType == 2) {
      // Only explicit write (2) is R/W; missing/unknown must stay read-only.
      userType = userDetails.accessType == 2 ? 'rwManager' : 'roManager';
    } else if (userType == 3) {
      userType = 'owner';
    } else if (userType == 4) {
      userType = 'generic';
      navigate('/premiumUser');
      return;
    } else if (userType == 5) {
      userType = 'voucherAdmin';
    } else if (userType == 6) {
      userType = 'preVoucherAdmin';
    } else if (userType == 7) {
      userType = 'secondaryAdmin';
    } else if (userType == 8) {
      userType = 'lineManager';
    }
    cookies.set('userRole', CryptoJS.AES.encrypt(userType, userDetails.email.split('@')[0].toLowerCase()).toString(), {
      secure: process.env.secureCoockie || true
    });
    cookies.set('userSubRole', CryptoJS.AES.encrypt('supportAgent', userDetails.email.split('@')[0].toLowerCase()).toString(), {
      secure: process.env.secureCoockie || true
    });
    localStorage.setItem('supportAgentUserId', localStorage.getItem('supportAgentUserId') || cookies.get('_Id'));
    localStorage.setItem('supportAgentAccountId', localStorage.getItem('supportAgentAccountId') || cookies.get('accountId'));
    cookies.set('accountId', userDetails.accountId, { secure: process.env.secureCoockie || true, path: '/' });
    cookies.set('userAccountId', cookies.get('_Id'), { secure: process.env.secureCoockie || true, path: '/' });
    localStorage.setItem('email', userDetails.email);
    localStorage.setItem('UserId', userDetails.oneCloudUserId);
    const supportLoginAsCookieOpts = { secure: process.env.secureCoockie || true };
    cookies.set('email', userDetails.email, supportLoginAsCookieOpts);
    cookies.set('user_email', userDetails.email, supportLoginAsCookieOpts);
    cookies.set('firstName', userDetails.firstname, { secure: process.env.secureCoockie || true });
    cookies.set('lastName', userDetails.lastname, { secure: process.env.secureCoockie || true });
    syncRumAuthenticatedSession({
      accountId: userDetails.accountId,
      email: userDetails.email,
      insightUserId: userDetails.oneCloudUserId,
      userRole: userType,
      actorType: 'support_agent',
      loginFlow
    });

    if (userDetails.userRole != 4) {
      cookies.set('isCountryDisabled', 0, { secure: process.env.secureCoockie || true });
      //if user is admin
      if (userDetails.userRole == '1') {
        if (userDetails.netAttachReq == '1') {
          await resolveLocationAssignmentThenRedirect(userDetails.email, { secure: process.env.secureCoockie || true });
        } else {
          cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', userDetails.email.split('@')[0].toLowerCase()).toString(), {
            secure: process.env.secureCoockie || true
          });
          redirectProAdmin();
        }
      } else {
        cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', userDetails.email.split('@')[0].toLowerCase()).toString(), {
          secure: process.env.secureCoockie || true
        });
        if (cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices') {
          cookies.set('toggleSrc', 'insightApp');
          // window.location.href = "/#/insightServices";
          cookies.remove('redirectPlatform');
          cookies.remove('redirectPage');
        } else if (cookies.get('existingProAcnt') == 'true') {
          // window.location.href = "/#/accountManagement/subScriptionsPro";
          cookies.remove('existingProAcnt');
        } else if (userDetails.userRole == '5') {
          getVoucherAdminLastVistedPage(userDetails.userRole);
        } else if (userDetails.userRole == '6') {
          getVoucherAdminLastVistedPage(userDetails.userRole);
        } else {
          checkOrganizationList();
        }
      }
    } else if (userDetails.userRole == 4 && isPayment) {
      cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', userDetails.email.split('@')[0].toLowerCase()).toString(), {
        secure: process.env.secureCoockie || true
      });
      // getUserDetailsPromise(); // leaving out this part as of now
    } else {
      if (cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices') {
        cookies.set('toggleSrc', 'insightApp', { secure: process.env.secureCoockie || true });
        // window.location.href = "/#/insightServices";
        cookies.remove('redirectPlatform');
        cookies.remove('redirectPage');
      } else {
        localStorage.setItem('no_subs_call', '1');
        // window.location.href = "/#/dashboard/account";
        checkOrganizationList();
      }
    }
  };
  const getApiKey = async () => {
    // user_email and userId are set by checkAuthWithCookies (auth/me) before calling getApiKey
    // If userToLogin exists, it means START_SUPPORT_SESSION was already called
    // Just maintain the session using userToLogin data without calling postUserLogin
    if (localStorage.getItem('userToLogin')) {
      loginAsUser();
      return;
    } else {
      // Keep loader visible until we navigate or handle error (avoids login screen flash after auth portal redirect)
      // Use userId from current session (localStorage set by auth/me) so we never send a stale cookie value
      // when two userId cookies exist (e.g. new account + verify + login with old cookie from parent domain).
      const sessionEmail = localStorage.getItem('user_email') || cookies.get('user_email');
      const sessionUserId = localStorage.getItem('userId') || cookies.get('userId');
      if (!sessionEmail || !sessionUserId) {
        hideLoader();
        window.location.href = getAuthPortalLoginUrl();
        return;
      }
      const data = {
        userLogin: {
          email: sessionEmail,
          deviceId: 'a193da5c0f68da21',
          deviceToken:
            'c7nETF26Vw8:APA91bHm8JtOA-sO-3TBMhv9q7e1LU4J-6AX9IYmm9xFfQ6x5T4zKzZs8UlLrvQ1KxSIxxOfl1kYRmL6eech_6G6W0hMFAZcfrCwqVJr8WUK30LK4fcW7zBVdnnoaiCempCocUzLbw1b',
          devicePlateform: 'WEB',
          userId: sessionUserId
        }
      };
      try {
        (
          await axios.get(`${process.env.apiHost}public/v1/apikey`, {
            headers: {
              'Content-Type': 'application/json; charset=UTF-8',
              apikey: process.env.apiKey,
              Accept: 'application/json'
            },
            withCredentials: true,
            timeout: 60000
          })
        ).data;
        // postUserLogin v2 (cookie-based)
        const result = (
          await axios.post(`${process.env.apiHost}user/v2/postUserLogin`, data, {
            headers: {
              apikey: process.env.apiKey,
              'Content-Type': 'application/json; charset=UTF-8'
            },
            withCredentials: true,
            timeout: 600000
          })
        ).data;
        if (result?.status == true) {
          localStorage.removeItem('postUserLoginFailMessage');
          // Redirect based on muiStatus (muiWebSupport): "1" = MUI, "0" = classic. Always call the API; never use postUserLogin response for this.
          let muiWebSupport;
          let tourCompleted = true;
          try {
            // muiStatus API only needs apiKey (header) and token (via cookie). Set token in cookie so it's sent with request.
            const token = result?.data?.token ?? result?.token ?? localStorage.getItem('token') ?? localStorage.getItem('access_token');
            if (token) {
              const cookieOpts = { path: '/', secure: !!process.env.secureCoockie };
              cookies.set('token', token, cookieOpts);
            }
            const muiStatusRes = (
              await axios.get(`${process.env.apiHost}public/v1/muiStatus/${result.data._id}`, {
                headers: {
                  'Content-Type': 'application/json; charset=UTF-8',
                  Accept: 'application/json',
                  apiKey: process.env.apiKey
                },
                withCredentials: true,
                timeout: 60000
              })
            ).data;
            const muiDetails = muiStatusRes?.response?.details ?? muiStatusRes?.details ?? {};
            // Prefer response.details (common API wrapper); fallback to top-level details
            muiWebSupport = muiDetails.muiWebSupport;
            if (muiDetails && Object.prototype.hasOwnProperty.call(muiDetails, 'tourCompleted')) {
              tourCompleted = muiDetails.tourCompleted === true;
            }
          } catch (muiErr) {
            console.error('[muiStatus] API call failed', muiErr);
            hideLoader();
            window.location.href = getAuthPortalLoginUrl();
            return;
          }
          const muiVal = muiWebSupport != null && muiWebSupport !== '' ? String(muiWebSupport).trim() : '';
          if (muiVal === '0') {
            // Set same session cookies as MUI flow so classic app sees user as logged in (avoids logout → classic login redirect).
            // Use path: '/' so cookies are sent when user is on /classic/ (default path can be current path only).
            const secureCookie = process.env.secureCoockie || true;
            const cookieOpts = { secure: secureCookie, path: '/' };
            cookies.set('email', result.data.email, cookieOpts);
            cookies.set('userid', result.data.userId, cookieOpts);
            cookies.set('user_email', result.data.email, cookieOpts);
            cookies.set('userId', result.data.userId ?? result.data.preferredUsername, cookieOpts);
            const loginFirstName = sanitizePersonName(result.data.firstName);
            const loginLastName = sanitizePersonName(result.data.lastName);
            if (loginFirstName) {
              cookies.set('firstName', loginFirstName, cookieOpts);
            }
            if (loginLastName) {
              cookies.set('lastName', loginLastName, cookieOpts);
            }
            cookies.set('_Id', result.data._id, cookieOpts);
            cookies.set('redirectCookies', '1', cookieOpts);
            cookies.set('sessionActive', '1', cookieOpts);
            localStorage.setItem('showHiddenBanner', result.data.showHiddenBanner);
            localStorage.setItem('muiAccountStatus', 'nonMigratedUser');
            let accountId = '';
            let userAccountId = '';
            if (result.data.UserRole && result.data.UserRole[0]) {
              let userType = result.data.UserRole[0].role;
              if (userType == 1) userType = 'admin';
              else if (userType == 2) userType = result.data.accessType == 2 ? 'rwManager' : 'roManager';
              else if (userType == 3) userType = 'owner';
              else if (userType == 4) userType = 'generic';
              else if (userType == 5) userType = 'voucherAdmin';
              else if (userType == 6) userType = 'preVoucherAdmin';
              else if (userType == 7) userType = 'secondaryAdmin';
              else if (userType == 8) userType = 'lineManager';
              if (
                supportAccessLevel.indexOf(result.data.accessLevel) >= 0 &&
                cookies.get('redirectPlatform') != 'insightApp' &&
                cookies.get('redirectPage') != 'insightServices' &&
                !goToMyAccIntentRef.current
              ) {
                userType = 'supportAgent';
              }
              cookies.set(
                'userRole',
                CryptoJS.AES.encrypt(userType, result.data.email?.split('@')[0]?.toLowerCase() || '').toString(),
                cookieOpts
              );
              if (
                result.data.UserRole[0].role == '2' ||
                result.data.UserRole[0].role == '3' ||
                result.data.UserRole[0].role == '5' ||
                result.data.UserRole[0].role == '6' ||
                result.data.UserRole[0].role == '8'
              ) {
                accountId = result.data.UserRole[0].parentId;
                userAccountId = result.data._id;
              } else if (result.data.UserRole[0].role == '7') {
                accountId = result.data.UserRole[0].parentId;
                userAccountId = result.data.UserRole[0].parentId;
              } else {
                accountId = result.accountId ?? '';
                userAccountId = result.accountId ?? '';
              }
            }
            cookies.set('accountId', accountId, cookieOpts);
            cookies.set('userAccountId', userAccountId, cookieOpts);
            cookies.set('supportAccessSt', supportAccessStForAccount(accountId, { data: result.data }), cookieOpts);
            const encKey = result.data.email?.split('@')[0]?.toLowerCase() || '';
            cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', encKey).toString(), cookieOpts);
            localStorage.setItem('email', result.data.email);
            localStorage.setItem('UserId', result.data.userId);
            if (result.data.isoCountryCode) localStorage.setItem('userCountry', result.data.isoCountryCode);
            if (result.data.accessLevel) localStorage.setItem('accessLevel', result.data.accessLevel);
            if (result.data.accessLevel && supportAccessLevel.indexOf(result.data.accessLevel) >= 0) {
              localStorage.setItem('supportAgentUserId', result.data._id);
              emitSupportSessionTtlFromPostUserLogin(result.data);
            }
            const baseUrl = (process.env.camSdkRedirectUrl || window.location.origin + (window.location.pathname || '/')).replace(
              /\/?$/,
              ''
            );
            const supportClassic =
              supportAccessLevel.indexOf(result.data.accessLevel) >= 0 &&
              cookies.get('redirectPlatform') != 'insightApp' &&
              cookies.get('redirectPage') != 'insightServices' &&
              !goToMyAccIntentRef.current;
            let classicRoute = 'organization/dashboard';
            if (supportClassic) {
              if (result.data.supportUserInfo && result.data.supportUserInfo.sessionStatus == '1') {
                if (result.data.supportUserInfo.sessionSecondsRemaining !== undefined) {
                  emitSupportSessionTtl(result.data.supportUserInfo, 'postUserLogin-resume');
                }
                persistRumLoginFlow('support_resume');
                localStorage.setItem(
                  'userToLogin',
                  JSON.stringify({
                    firstname: result.data.supportUserInfo.firstName,
                    email: result.data.supportUserInfo.email,
                    accountId: result.data.supportUserInfo.custAccId,
                    lastname: result.data.supportUserInfo.lastName,
                    userRole: result.data.supportUserInfo.custUserRole,
                    oneCloudUserId: result.data.supportUserInfo.oneCloudUserId,
                    accessType: result.data.supportUserInfo.accessType,
                    netAttachReq: result.netAttachReq
                  })
                );
              } else {
                localStorage.removeItem('userToLogin');
                classicRoute = 'support/usersList';
              }
            }
            const classicDashboardUrl = `${baseUrl}/classic/#/${classicRoute}`;
            hideLoader();
            // Redirect via auth portal when available so classic gets valid auth session (avoids 401 → logout on classic)
            if (process.env.authUrl) {
              window.location.href = getAuthPortalLoginUrl(classicDashboardUrl);
            } else {
              window.location.href = classicDashboardUrl;
            }
            return;
          }
          if (muiVal !== '1') {
            hideLoader();
            window.location.href = getAuthPortalLoginUrl();
            return;
          }
          // muiWebSupport "1" → continue MUI flow; optional guided tour when backend says first login tour not done
          setEmailID(result.data.email);
          cookies.set('email', result.data.email, { secure: cookieSecure(), path: '/' });
          cookies.set('userid', result.data.userId, { secure: cookieSecure(), path: '/' });
          const loginFirstName = sanitizePersonName(result.data.firstName);
          const loginLastName = sanitizePersonName(result.data.lastName);
          if (loginFirstName) {
            cookies.set('firstName', loginFirstName, { secure: cookieSecure(), path: '/' });
          }
          if (loginLastName) {
            cookies.set('lastName', loginLastName, { secure: cookieSecure(), path: '/' });
          }
          localStorage.setItem('muiAccountStatus', muiVal == '1' ? 'MigratedUser' : 'nonMigratedUser');
          localStorage.setItem('showHiddenBanner', result.data.showHiddenBanner);
          if (result.data.businessDetFlag == 1) {
            // $(".loaderContainerBlock").hide()
            // $(".warningModal").modal("show");
            return;
          }
          let accountId = '';
          let userAccountId = '';
          let userType = '';
          if (result.data.isoCountryCode == '') {
            // $(".countryNotFoundModal").modal("show");
            // $(".loaderContainer").hide();//hide loader to unblock user
            return;
          }

          const accountContexts = parseAccountContextsFromLoginResponse(result);
          persistAccountContexts(accountContexts);
          const loginPayload = {
            data: result.data,
            accountId: result.accountId || result.data?.accountId,
            accessType: result.data.accessType,
            tourCompleted
          };
          persistLoginSnapshot(loginPayload);

          cookies.set('_Id', result.data._id, { secure: cookieSecure(), path: '/' });

          if (shouldShowAccountPicker(accountContexts, result)) {
            persistPostLoginPending(loginPayload);
            hideLoader();
            navigate('/account-selection', { replace: true, state: { contexts: accountContexts } });
            return;
          }

          const isSupportLanding =
            result.data.accessLevel &&
            supportAccessLevel.indexOf(result.data.accessLevel) >= 0 &&
            cookies.get('redirectPlatform') != 'insightApp' &&
            cookies.get('redirectPage') != 'insightServices' &&
            !goToMyAccIntentRef.current &&
            !(result.data.supportUserInfo && result.data.supportUserInfo.sessionStatus == '1');

          const activeContext = getActiveContext(accountContexts) || getDefaultContext(accountContexts, result.data.defaultContext);
          const roleEntry = getUserRoleEntryForContext(result.data.UserRole, activeContext) || result.data.UserRole?.[0];
          const contextToApply = isSupportLanding ? null : activeContext || buildContextFromRoleEntry(roleEntry, loginPayload);
          if (isSupportLanding) {
            accountId = result.accountId || result.data?.accountId || accountId;
            userAccountId = result.data._id || userAccountId;
            userType = 'supportAgent';
            // Must overwrite a leftover impersonation accountId. START_SUPPORT_SESSION
            // sends this cookie as the header; a stale customer id fails validation
            // and blocks every impersonation.
            if (accountId) {
              cookies.set('accountId', accountId, { secure: cookieSecure(), path: '/' });
            }
            if (userAccountId) {
              cookies.set('userAccountId', userAccountId, { secure: cookieSecure(), path: '/' });
            }
            localStorage.setItem('supportAgentAccountId', accountId || '');
            cookies.set(
              'userRole',
              CryptoJS.AES.encrypt('supportAgent', result.data.email?.split('@')[0]?.toLowerCase() || '').toString(),
              { secure: cookieSecure(), path: '/' }
            );
          }
          if (contextToApply) {
            applyAccountContext(contextToApply, loginPayload, cookies);
            accountId = cookies.get('accountId') || contextToApply.accountId || accountId;
            userAccountId = cookies.get('userAccountId') || userAccountId;
            userType = resolveUserRoleKey(contextToApply.role, contextToApply.accessType);
            // Must use goToMyAccIntentRef, not localStorage.goToMyAcc: the flag is
            // consumed and removed on mount. Checking localStorage here would always
            // overwrite the real Insight role with supportAgent, causing
            // roleManagement.checkUser() to deny every write on "Go to my account".
            if (
              result.data.accessLevel &&
              supportAccessLevel.indexOf(result.data.accessLevel) >= 0 &&
              cookies.get('redirectPlatform') != 'insightApp' &&
              cookies.get('redirectPage') != 'insightServices' &&
              !goToMyAccIntentRef.current
            ) {
              userType = 'supportAgent';
              cookies.set(
                'userRole',
                CryptoJS.AES.encrypt('supportAgent', result.data.email?.split('@')[0]?.toLowerCase() || '').toString(),
                { secure: cookieSecure(), path: '/' }
              );
            }
          }
          localStorage.setItem('email', result.data.email);
          localStorage.setItem('UserId', result.data.userId);
          if (result.data.accessLevel && supportAccessLevel.indexOf(result.data.accessLevel) >= 0) {
            localStorage.setItem('supportAgentUserId', result.data._id);
          }
          localStorage.setItem('userCountry', result.data.isoCountryCode);
          localStorage.setItem('loggedInFirstTime', true);
          cookies.set('_Id', result.data._id, { secure: cookieSecure(), path: '/' });
          if (!contextToApply) {
            cookies.set('supportAccessSt', supportAccessStForAccount(accountId, loginPayload), { secure: cookieSecure(), path: '/' });
          }

          //set Access Level
          // this.props.dispatch(setAccessLevel(result.data.accessLevel));
          localStorage.setItem('accessLevel', result.data.accessLevel);
          if (supportAccessLevel.indexOf(result.data.accessLevel) >= 0) {
            emitSupportSessionTtlFromPostUserLogin(result.data);
          }
          setRumSessionIdentity({
            accountId,
            userEmail: result.data.email,
            insightUserId: result.data.userId,
            userRole: userType,
            actorType: resolveRumActorType(result.data.accessLevel)
          });
          //   this.getSupportedFeatureList()
          // check whether the user is generic or not
          if (
            supportAccessLevel.indexOf(result.data.accessLevel) >= 0 &&
            cookies.get('redirectPlatform') != 'insightApp' &&
            cookies.get('redirectPage') != 'insightServices' &&
            !goToMyAccIntentRef.current
          ) {
            // Active support session -> resume the customer's account instead of the support list.
            if (result.data.supportUserInfo && result.data.supportUserInfo.sessionStatus == '1') {
              if (result.data.supportUserInfo.sessionSecondsRemaining !== undefined) {
                emitSupportSessionTtl(result.data.supportUserInfo, 'postUserLogin-resume');
              }
              persistRumLoginFlow('support_resume');
              localStorage.setItem(
                'userToLogin',
                JSON.stringify({
                  firstname: result.data.supportUserInfo.firstName,
                  email: result.data.supportUserInfo.email,
                  accountId: result.data.supportUserInfo.custAccId,
                  lastname: result.data.supportUserInfo.lastName,
                  userRole: result.data.supportUserInfo.custUserRole,
                  oneCloudUserId: result.data.supportUserInfo.oneCloudUserId,
                  accessType: result.data.supportUserInfo.accessType,
                  netAttachReq: result.netAttachReq
                })
              );
              // Call loginAsUser() directly; navigate('/') is a no-op here (already on '/').
              await loginAsUser();
              return;
            }
            consumeRumLoginFlow('direct');
            syncRumAuthenticatedSession({
              accountId,
              email: result.data.email,
              insightUserId: result.data.userId,
              userRole: userType,
              actorType: resolveRumActorType(result.data.accessLevel),
              loginFlow: 'direct'
            });
            localStorage.removeItem('userToLogin');
            cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', emailID.split('@')[0].toLowerCase()).toString());
            hideLoader();
            navigate('./support/usersList');
            return;
          }
          consumeRumLoginFlow('direct');
          syncRumAuthenticatedSession({
            accountId,
            email: result.data.email,
            insightUserId: result.data.userId,
            userRole: userType,
            actorType: resolveRumActorType(result.data.accessLevel),
            loginFlow: 'direct'
          });
          if (goToMyAccIntentRef.current && result.data.supportUserInfo && result.data.supportUserInfo.sessionStatus == '1') {
            // Matches the imuserportal legacy UI: reaching the Insight account while a
            // support session is active is purely informative — we do NOT end the session
            // here. Consume the one-shot intent so the dialog's OK resumes the active
            // session on the next auth pass.
            goToMyAccIntentRef.current = false;
            hideLoader();
            setActiveSessionDialog({ open: true });
            return;
          }
          goToMyAccIntentRef.current = false;
          if (!(cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices')) {
            // this.props.dispatch(setWhatsNew(result.data.whatsNewKey));
          }
          cookies.remove('userSubRole', { secure: process.env.secureCoockie || true });
          if (roleEntry && roleEntry.role != 4) {
            cookies.set('isCountryDisabled', 0, { secure: process.env.secureCoockie || true });
            //if user is admin
            if (roleEntry.role == '1') {
              if (result.netAttachReq && result.netAttachReq == '1') {
                await resolveLocationAssignmentThenRedirect(result.data.email, undefined, tourCompleted === false);
              } else {
                cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', emailID.split('@')[0].toLowerCase()).toString());
                redirectProAdmin(tourCompleted === false);
              }
            } else {
              cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', emailID.split('@')[0].toLowerCase()).toString());
              if (cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices') {
                cookies.set('toggleSrc', 'insightApp');
                // window.location.href = "/#/insightServices";
                cookies.remove('redirectPlatform');
                cookies.remove('redirectPage');
              } else if (cookies.get('existingProAcnt') == 'true') {
                // window.location.href = "/#/accountManagement/subScriptionsPro";
                cookies.remove('existingProAcnt');
              } else if (roleEntry.role == '5') {
                getVoucherAdminLastVistedPage(roleEntry.role);
              } else if (roleEntry.role == '6') {
                getVoucherAdminLastVistedPage(roleEntry.role);
              } else {
                // window.location.href = "/#/organization/dashboard";
                checkOrganizationList(tourCompleted === false);
              }
            }
          } else if (roleEntry && roleEntry.role == 4 && isPayment) {
            cookies.set('isLocNeedToAssg', CryptoJS.AES.encrypt('no', emailID.split('@')[0].toLowerCase()).toString());
            // getUserDetailsPromise(); // leaving out this part as of now
          } else {
            if (cookies.get('redirectPlatform') == 'insightApp' && cookies.get('redirectPage') == 'insightServices') {
              cookies.set('toggleSrc', 'insightApp');
              // window.location.href = "/#/insightServices";
              cookies.remove('redirectPlatform');
              cookies.remove('redirectPage');
            } else {
              localStorage.setItem('no_subs_call', '1');
              // window.location.href = "/#/dashboard/account";
              checkOrganizationList(tourCompleted === false);
            }
          }
        } else {
          if (result?.meta?.code == '400') {
            localStorage.setItem('postUserLoginFailMessage', intl.formatMessage({ id: 'postUserLoginFailedErr400' }));
          } else {
            localStorage.setItem('postUserLoginFailMessage', intl.formatMessage({ id: 'postUserLoginFailedErr' }));
          }
          hideLoader();
          navigate('/failSafeSupport');
          return;
        }
      } catch (err) {
        // Only bounce through the auth portal for actual auth failures; other errors
        // shouldn't be mistaken for a logged-out session.
        console.error('Error fetching API key:', err);
        hideLoader();
        const isAxiosError = !!(err && (err.isAxiosError || err.response || err.request));
        const status = err?.response?.status;
        const isAuthFailure = isAxiosError && (!status || status === 401 || status === 403);
        if (isAuthFailure) {
          window.location.href = getAuthPortalLoginUrl();
        } else {
          // Surface unexpected client-side errors on failSafeSupport instead of looping.
          localStorage.setItem('postUserLoginFailMessage', intl.formatMessage({ id: 'postUserLoginFailedErr' }));
          navigate('/failSafeSupport');
        }
        return;
      }
    }
  };

  // Informative dialog acknowledgement. The support session is left intact; resume the
  // active support session on the next auth pass (matches the imuserportal legacy UI).
  const handleActiveSessionAck = () => {
    goToMyAccIntentRef.current = false;
    setActiveSessionDialog({ open: false });
    showLoader();
    checkAuthWithCookies();
  };

  return (
    <Dialog
      open={activeSessionDialog.open}
      onClose={handleActiveSessionAck}
      id="activeSessionDialog"
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          textAlign: 'center',
          p: 5
        }
      }}
    >
      <DialogContent id="activeSessionDialog-content" sx={{ p: 0 }}>
        <IconButton
          onClick={handleActiveSessionAck}
          size="small"
          sx={{ position: 'absolute', right: 30, top: 30, zIndex: 1 }}
          id="close-activeSessionDialog-button"
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 5,
            width: '87px',
            height: '87px',
            minHeight: '87px',
            borderRadius: '100%',
            p: 2,
            backgroundColor: '#E3F2FD'
          }}
        >
          <img src={questionMarkDarkTheme} alt="dialog" style={{ width: '47px' }} />
        </Box>
        <DialogContentText id="activeSessionDialog-contentText">
          <Typography variant="body1" sx={{ color: 'grey.700' }}>
            <FormattedMessage
              id="sessionErrMsg"
              defaultMessage="You have an active user impersonation session open. You must end the first session before you can begin another."
            />
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions
        id="activeSessionDialog-actions"
        sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
      >
        <Button onClick={handleActiveSessionAck} autoFocus id="activeSessionDialog-okBtn" variant="contained" sx={{ minWidth: '86px' }}>
          <FormattedMessage id="ok" defaultMessage="OK" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default CamSDK;
